<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" id="examConsentOffcanvas" data-bs-backdrop="static" aria-labelledby="examConsentOffcanvasLabel" ref="offcanvasElement">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" @click="doCancel">
        <IconArrowBack />
        {{ step === 'success' ? $t('close') : $t('goBack') }}
      </button>

      <div class="">
        <h5 class="offcanvas-title d-block no-wrap">{{ $t('examConsent.title') }}</h5>

        <div v-if="step !== 'success' && step !== 'loading'">
          <div class="switch-language-box" v-if="!hasCustomTemplates">
            <div class="btn-group" role="group" aria-label="Switch language">
              <input type="radio" class="btn-check" name="switchLanguage" id="switchLanguageEN" autocomplete="off" :checked="consentLang === 'en'"/>
              <label class="btn btn-outline-secondary" for="switchLanguageEN" @click="switchLang('en')">EN</label>

              <input type="radio" class="btn-check" name="switchLanguage" id="switchLanguageES" autocomplete="off" :checked="consentLang === 'es'"/>
              <label class="btn btn-outline-secondary no-border-radius" for="switchLanguageES" @click="switchLang('es')">ES</label>

              <template v-if="isGlobalClient">
                <input type="radio" class="btn-check" name="switchLanguage" id="switchLanguagePTBR" autocomplete="off" :checked="consentLang === 'pt-br'"/>
                <label class="btn btn-outline-secondary" for="switchLanguagePTBR" @click="switchLang('pt-br')">PT-BR</label>
              </template>
              <template v-else>
                <input type="radio" class="btn-check" name="switchLanguage" id="switchLanguageHT" autocomplete="off" :checked="consentLang === 'ht'"/>
                <label class="btn btn-outline-secondary" for="switchLanguageHT" @click="switchLang('ht')">HT</label>
              </template>
            </div>
          </div>

          <div class="form-floating v-select-floating template-select-box" v-else>
            <v-select 
              class="v-select form filter" 
              v-model="selectedTemplate" 
              data-test="vSelectConsentTemplate" 
              :placeholder="$t('examConsent.selectTemplate')"
              :reduce="x => x.value"
              :options="formattedTemplates"
              :append-to-body="false" 
              @option:selected="goToSignature"
            />
            <label for="title">{{ $t('examConsent.selectTemplate') }}</label>
          </div>
        </div>
      </div>
    </div>

    <div class="offcanvas-body" v-loader.opacity="loading">
      <div class="no-results-box" v-if="step === 'no-templates'">
        <BaseNoResults :title="$t('examConsent.noTemplatesConfigured')" :text="$t('examConsent.configureTemplatesHint')"/>
      </div>

      <div v-if="step === 'signature'" class="signature-box">
        <div>
          <h6>{{ $t('examConsent.previewTitle') }}</h6>
          <div class="text-preview-box scrollbar" v-html="processedHtml">
          </div>
        </div>

        <div>
          <h6>{{ $t('examConsent.patientSignature') }}</h6>
          <div class="signature-dropzone">
            <div ref="signaturePad"></div>
            <button class="btn-remove-signature" @click="clearSignature" v-if="!loading">
              <IconCross />
            </button>
          </div>
        </div>
      </div>

      <div v-if="step === 'success'" class="success-box text-center py-5">
        <IconCheckBig/>

        <div class="">
          <h1>{{ $t('examConsent.successTitle') }}</h1>
          <h6>{{ $t('examConsent.successMessage') }}</h6>
        </div>

        <button class="btn btn-success" type="button" data-test="btnDownloadPdf" @click="handleDownloadSuccess">
          {{ $t('examConsent.downloadSigned') }}
        </button>
      </div>

      <div class="offcanvas-footer" v-if="step !== 'success' && step !== 'loading' && step !== 'no-templates'">
        <div v-if="step === 'signature'">
          <p v-if="!isTemplateValid"
            class="invalid-template-alert">
            {{ $t('examConsent.invalidTemplateTags') }}
          </p>

          <button v-if="step === 'signature'"
                  class="btn btn-success"
                  type="button"
                  data-test="btnSaveSigned"
                  @click="saveFinalDocument"
                  :disabled="!isTemplateValid">
            {{ $t('examConsent.saveSignedDocument') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// needs to sit above the raised backdrop set in styles/global/_offcanvas.scss (1047)
// to stay interactive instead of being covered by it, when opened on top of another offcanvas.
#examConsentOffcanvas {
  z-index:                             1048;
}

.offcanvas {
  width:                              90% !important;

  .offcanvas-header {
    > div {
      display:                        flex;
      flex-direction:                 row;
      justify-content:                space-between;
      align-items:                    center;
    }

    .switch-language-box {
      display:                        flex;
      flex-direction:                 row;
      align-items:                    center;
    }

    .template-select-box {
      width:                          300px;

      :deep(.vs__dropdown-menu) {
        top:                          calc(100% + 4px) !important;
        bottom:                       auto !important;
      }
    }

    .btn-group {
      .btn-outline-secondary:not(.dropdown-toggle) {
        @include                      border_radius(99px 0 0 99px);
        color:                        var(--Shades-70);
        font-size:                    var(--font-size-12);
        font-weight:                  400;
        line-height:                  normal;
        letter-spacing:               1.8px;
        text-transform:               uppercase;
        text-wrap:                    nowrap;
        display:                      flex;
        justify-content:              flex-end;
        align-items:                  center;
        gap:                          10px;
        margin:                       0;
        padding:                      10px 15px;
        background:                   var(--Shades-W);
        border:                       1px solid var(--Shades-30-Border);

        &:focus,
        &:hover,
        &:active,
        &.active {
          color:                      var(--Complementary-Success);
          background:                 var(--Complementary-Success-BG);
          border:                     1px solid var(--Complementary-Success);
        }

        &.no-border-radius {
          @include                    border_radius(0);
        }

        &:last-child {
          @include                    border_radius(0 99px 99px 0);
          border-left:                0;
        }
      }

      > div {
        .btn-outline-secondary:not(.dropdown-toggle) {
          @include                    border_radius(99px 0 0 99px);
          border:                     1px solid var(--Shades-30-Border);
        }
      }

      .btn-check:checked+.btn-outline-secondary {
        color:                        var(--Complementary-Success);
        background:                   var(--Complementary-Success-BG);
        border:                       1px solid var(--Complementary-Success) !important;
      }
    }
  }

  .offcanvas-body {
    display:                          flex;
    flex-direction:                   column;
    gap:                              26px;
    overflow:                         hidden;

    h6 {
      font-size:                      var(--font-size-18) !important;
      color:                          var(--Brand-Primary) !important;
    }
  }

  .signature-dropzone {
    @include                          border_radius(10px);
    position:                         relative;
    overflow:                         hidden;
    max-height:                       100px;
    background:                       #fff;
    border:                           1px solid var(--Shades-60-Border);

    .btn-remove-signature {
      position:                       absolute;
      top:                            10px;
      right:                          10px;
      background:                     transparent;
      border:                         none;
      z-index:                        5;
    }
  }

  .signature-box {
    display:                          flex;
    flex-direction:                   column;
    gap:                              26px;
    flex:                             1;
    min-height:                       0;

    > div {
      &:first-child {
        flex:                         1;
        min-height:                   0;
        display:                      flex;
        flex-direction:               column;
        overflow:                     hidden;
      }

      &:last-child {
        flex:                         0 0 auto;
      }
    }
  }

  .text-preview-box {
    @include                          border_radius(10px);
    flex:                             1;
    min-height:                       0;
    padding:                          20px;
    overflow-y:                       auto;
    background:                       #fff;
    border:                           1px solid var(--Shades-60-Border);

    :deep(*) {
      font-size:                      var(--font-size-14);
      color:                          #000 !important;
    }

    :deep(p) {
      margin:                         4px 0 !important;
      line-height:                    1.5;
    }

    :deep(h2) {
      font-size:                      var(--font-size-18);
    }

    :deep(h4) {
      font-size:                      var(--font-size-16);
    }
  }

  .success-box {
    display:                          flex;
    flex-direction:                   column;
    flex:                             1;
    align-items:                      center;
    justify-content:                  center;
    gap:                              26px;
    max-width:                        500px;
    margin:                           0 auto;

    h1,
    h6 {
      color:                          var(--Brand-Primary);
    }

    h1 {
      font-size:                      38px;
      font-weight:                    400;
      line-height:                    normal;
    }

    h6 {
      font-size:                      var(--font-size-14);
      font-weight:                    300;
      line-height:                    140%;
    }
  }

  .offcanvas-footer {
    display:                          flex;
    flex-direction:                   column;
    gap:                              26px;
  }

  .btn-success {
    font-size:                        var(--font-size-14);
    font-weight:                      700;
    line-height:                      normal;
    letter-spacing:                   4.2px;
    text-align:                       center;
    text-transform:                   uppercase;
    width:                            100%;
    max-width:                        350px;
    margin:                           0;
    padding:                          20px;
  }

  .invalid-template-alert {
    color:                            red !important;
    margin:                           0 !important;
    margin-bottom:                    5px !important;
  }
}

@media (max-width: 991px) {
  .offcanvas {
    width:                            100% !important;
    max-width:                        unset !important;
  }
}

@media (max-width: 767px) {
  .offcanvas {
    .offcanvas-header {
      overflow:                       hidden;

      h5 {
        margin:                       0 0 5px;
      }

      > div {
        flex-direction:               column;
        gap:                          5px;
        justify-content:              flex-start;
        align-items:                  flex-start;
      }
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user';
import signature from '@lemonadejs/signature';
import EyercloudConstants from '@/utils/constants.js';
import { TAG_DICTIONARY } from '@/utils/consentTagsDictionary';

export default {
  name: 'ExamConsentOffcanvas',
  data: () => ({
    offcanvas_instance: null,
    loading: false,
    step: 'loading',
    examId: null,
    patientId: null,
    autoCreateExam: false,
    finalPdfUrl: null,
    consentTemplates: [],
    selectedTemplate: null,
    resolvePromise: null,
    session: {},
    consentLang: 'en',
    patientData: null,
    examData: null,
    availableLocales: [],
    currentClinicLocale: 'en',
  }),
  watch: {
    selectedTemplate(newTemplate) {
      if (newTemplate) {
        this.consentLang = newTemplate.language || this.currentClinicLocale;
      }
    }
  },
  computed: {
    isTemplateValid() {
      if (!this.selectedTemplate || !this.selectedTemplate.htmlContent)
        return false;

      const content = this.selectedTemplate.htmlContent;
      
      const hasSignature = this.availableLocales.some(locale => {
        const translatedTag = `{{${this.$i18n.t('consentTags.system.signatureImg', locale)}}}`;
        return content.includes(translatedTag);
      });

      return hasSignature;
    },
    processedHtml() {
      if (!this.selectedTemplate || !this.selectedTemplate.htmlContent)
        return '';

      let html = this.selectedTemplate.htmlContent;
      const payload = this.buildPayload();

      Object.keys(payload).forEach(key => {
        html = html.split(`{{${key}}}`).join(payload[key]);
      });

      return html.replace(/{{.*?}}/g, '');
    },
    isGlobalClient() {
      const userStore = useUserStore();
      return userStore.sessionInfo?.isGlobalClient ?? false;
    },
    defaultTemplates() {
      return (this.consentTemplates || []).filter(t => t.isDefault);
    },
    customTemplates() {
      return (this.consentTemplates || []).filter(t => !t.isDefault);
    },
    hasCustomTemplates() {
      return this.customTemplates.length > 0;
    },
    formattedTemplates() {
      const all = [...this.customTemplates, ...this.defaultTemplates];
      
      return all.map(t => {
        let labelText = t.name;

        if (t.isDefault) {
          labelText = `${t.name} (${(t.language || '').toUpperCase()})`;
        }

        return {
          label: labelText,
          value: t
        };
      });
    }
  },
  mounted() {
    const userStore = useUserStore();
    this.session = userStore.sessionInfo;

    this.availableLocales = this.$i18n.availableLocales;
    this.currentClinicLocale = this.session.currentClinic.locale;
  },
  methods: {
    async show(options = {}) {
      this.resetForm();
      this.examId = options.examId || null;
      this.patientId = options.patientId || null;
      this.autoCreateExam = options.autoCreateExam || false;

      const el = this.$refs.offcanvasElement;
      if (el) {
        this.offcanvas_instance = bootstrap.Offcanvas.getOrCreateInstance(el);
        this.offcanvas_instance.show();
      }
      
      await this.findExamAndPatient();

      await this.loadTemplates();

      return new Promise((resolve) => { this.resolvePromise = resolve; });
    },
    async findExamAndPatient() {
      this.loading = true;
      try {
        if (this.patientId) {
          const patientResponse = await this.$patientService.getPatient({ id: this.patientId });
          this.patientData = patientResponse.patient || patientResponse;
        }
        
        if (this.examId) {
          const examResponse = await this.$examService.get({ id: this.examId });
          this.examData = examResponse.exam || examResponse;
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const response = await this.$clinicService.listConsentTemplates({
          clinicID: this.session.currentClinic.id
        });

        this.consentTemplates = response.result || [];

        if (this.consentTemplates.length) {
          if (this.hasCustomTemplates) {
            this.selectTemplate(this.customTemplates[0]);
          } else {
            const defaultTemplate = this.defaultTemplates.find(template => template.language === this.currentClinicLocale);
            this.selectTemplate(defaultTemplate || this.defaultTemplates[0]);
            
            this.consentLang = this.selectedTemplate.language;
          }

          this.goToSignature();
        } else {
          this.step = 'no-templates';
        }
      } catch (err) {
        this.step = 'no-templates';
      } finally {
        this.loading = false;
      }
    },
    switchLang(lang) {
      this.consentLang = lang;

      const templateToUse = this.defaultTemplates.find(template => template.language === lang);
      
      if (templateToUse) {
        this.selectTemplate(templateToUse);
      }
    },
    selectTemplate(template) {
      this.selectedTemplate = template;
    },
    goToSignature() {
      if (!this.selectedTemplate) return;
      this.step = 'signature';
      this.$nextTick(() => {
        this.initSignature();
      });
    },
    toTitleCase(str) {
      if (!str) return str;
      return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    buildPayload(signatureBase64 = '') {
      const formats = this.getFormatOptions();

      const signaturePayload = this.buildSignaturePayload(signatureBase64, formats);

      const contextData = {
        patient: this.patientData || {},
        clinic: this.session.currentClinic || {},
        exam: this.examData || {}
      };
      
      const schema = this.extractTemplateSchema();
      const dictionaryPayload = this.buildDictionaryPayload(schema, contextData, formats);

      return {
        ...dictionaryPayload,
        ...signaturePayload 
      };
    },
    getFormatOptions() {
      const dateTimeFormat = this.$i18n.t('dateTimeFormat', this.consentLang);
      const birthdayFormat = this.$i18n.t('birthdayFormat', this.consentLang);
      return {
        dateTimeFormat,
        birthdayFormat,
        currentDate: moment().format(dateTimeFormat)
      };
    },
    buildSignaturePayload(signatureBase64, formats) {
      const payload = {};
      let safeImageHtml = `<em>[ ${this.$i18n.t('examConsent.patientSignature', this.consentLang)} ]</em>`;
      
      if (signatureBase64) {
        const labelAssinatura = this.$i18n.t('clinic.digitallySigned', this.consentLang);
        safeImageHtml = `<img src="${signatureBase64}" style="max-height: 40px; width: auto;" /><br /><span style="font-size: 12px;">${labelAssinatura} ${formats.currentDate}</span>`;
      }
      
      payload.signature_date = formats.currentDate;

      this.availableLocales.forEach(locale => {
        const tagCurrentDate = this.$i18n.t('consentTags.system.currentDate', locale);
        const tagSignature = this.$i18n.t('consentTags.system.signatureImg', locale);
        
        if (tagCurrentDate) payload[tagCurrentDate] = formats.currentDate;
        if (tagSignature) payload[tagSignature] = safeImageHtml;
      });

      return payload;
    },
    extractTemplateSchema() {
      let schema = this.selectedTemplate?.formSchema || [];
      
      if (schema.length === 0 && this.selectedTemplate?.htmlContent) {
        const matches = this.selectedTemplate.htmlContent.match(/{{(.*?)}}/g) || [];
        schema = matches.map(m => ({ key: m.replace(/{{|}}/g, '').trim() }));
      }
      return schema;
    },
    buildDictionaryPayload(schema, contextData, formats) {
      const payload = {};
      const dateFieldsFormatMap = {
        'patient.birthday': formats.birthdayFormat,
        'exam.date': formats.dateTimeFormat
      };

      schema.forEach(field => {
        if (!field.key) return;
        
        const realPath = TAG_DICTIONARY[field.key] || field.key;
        let value = realPath.split('.').reduce((acc, part) => (acc ? acc[part] : null), contextData);

        if (realPath === 'patient.fullName') {
          value = this.toTitleCase(value);
        } else if (value && dateFieldsFormatMap[realPath]) {
          value = this.formatDateField(value, realPath, dateFieldsFormatMap[realPath]);
        }

        if (value !== null && value !== undefined && value !== '') {
          payload[field.key] = value;
        } else {
          payload[field.key] = '';
        }
      });

      return payload;
    },
    formatDateField(value, fieldPath, format) {
      if (!value) return '';

      const timezoneAwareFields = ['exam.date']; 
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const date = timezoneAwareFields.includes(fieldPath)
        ? moment.utc(value).tz(timezone)
        : moment.utc(value);

      if (date.isValid())
        return date.format(format);

      return value;
    },
    wrapHtmlContent(quillHtml) {
      const sanitizedHtml = quillHtml
        .replace(/<br>/g, '<br />');

      return `<!DOCTYPE html>
        <html>
        <head>
        <meta charset="UTF-8" />
        <style>
          html, body { margin: 0; padding: 0; font-family: sans-serif; font-size: 12px; color: #000; }
          @page { size: A4; margin: 25mm 20mm 25mm 20mm; }
          h1 { font-size: 18px; text-align: center; margin-bottom: 15px; }
          h2 { font-size: 15px; margin-bottom: 10px; }
          h3 { font-size: 13px; margin-bottom: 8px; }
          p { margin: 4px 0; line-height: 1.5; }
          ul, ol { margin: 4px 0 4px 20px; }
          .signature-line { border-bottom: 1px solid #000; width: 60%; height: 35px; margin-top: 30px; }
        </style>
        </head>
        <body>
        ${sanitizedHtml}
        </body>
        </html>`;
      },
    initSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.innerHTML = '';
        const containerWidth = this.$refs.signaturePad.clientWidth || 600;
        signature(this.$refs.signaturePad, {
          width: containerWidth,
          height: 100,
        });
      }
    },
    clearSignature() {
      this.initSignature();
    },
    ensureMinimumPayloadSize(payload, minBytes = 10000) {
      const json = JSON.stringify(payload);
      const currentSize = new TextEncoder().encode(json).length;

      if (currentSize >= minBytes) {
        return payload;
      }

      const paddingNeeded = minBytes - currentSize;

      payload.data._padding = "x".repeat(paddingNeeded);

      return payload;
    },
    async saveFinalDocument() {
      const canvas = this.$refs.signaturePad.querySelector('canvas');

      const isCanvasEmpty = (canvas) => {
        const blank = document.createElement('canvas');
        blank.width = canvas.width;
        blank.height = canvas.height;
        return canvas.toDataURL() === blank.toDataURL();
      };

      if (!canvas || isCanvasEmpty(canvas)) {
        this.$message.popup(this, this.$t('examConsent.signatureRequired'), 'operationFail', 'warn');
        return;
      }

      this.loading = true;

      try {
        if (this.autoCreateExam && this.patientId) {
          const examResponse = await this.$patientService.saveExam({
            id: this.patientId,
            exam: { date: new Date(), mydriasis: false }
          });
          this.examId = examResponse.result.id;
        }

        const signatureBase64 = canvas.toDataURL('image/png');
        const payload = this.buildPayload(signatureBase64);
        const fullHtml = this.selectedTemplate.htmlContent
          ? this.wrapHtmlContent(this.selectedTemplate.htmlContent)
          : null;

        let params = {
          htmlTemplate: fullHtml,
          data: payload,
          type: EyercloudConstants.EXAM_CONSENT_TYPE
        };

        params = this.ensureMinimumPayloadSize(params);

        const responseData = await this.$examDataService.generateConsentPdf(params);

        if (responseData.url && responseData.uuid) {
          const payload = {
            clinicID: this.session.currentClinic.id,
            examId: this.examId,
            type: 'OTHER_CONSENT_PDF',
            description: `${this.$i18n.t('examConsent.fileName')} - ${this.selectedTemplate?.name}`,
            uuid: responseData.uuid,
            s3Key: responseData.uuid,
            extension: 'pdf',
            originalName: `${this.$i18n.t('examConsent.fileName')}.pdf`
          };

          await this.$examDataService.saveOthers(payload);

          this.finalPdfUrl = responseData.url;
          this.step = 'success';

          if (this.resolvePromise) {
            this.resolvePromise(true);
          }

          this.$message.popup(this, this.$t('examConsent.successSaved'), 'operationSuccess', 'success');
        }
      } catch (err) {
        this.$message.popup(this, this.$t('examConsent.errorSavingDocument'), 'operationFail', 'error');
      } finally {
        this.loading = false;
      }
    },
    handleDownloadSuccess() {
      NProgress.start();
      this.loading = true;

      try {
        let el = document.createElement('a');
        el.href = this.finalPdfUrl;
        el.target = '_blank';
        el.rel = 'noopener noreferrer';

        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    resetForm() {
      this.step = 'loading';
      this.examId = null;
      this.patientId = null;
      this.autoCreateExam = false;
      this.patientData = null;
      this.examData = null;

      if (this.finalPdfUrl)
        URL.revokeObjectURL(this.finalPdfUrl);

      this.finalPdfUrl = null;
      this.selectedTemplate = null;
      this.consentTemplates = [];

      if (this.$refs.signaturePad)
        this.$refs.signaturePad.innerHTML = '';
    },
    doCancel() {
      if (this.offcanvas_instance)
        this.offcanvas_instance.hide();

      this.resetForm();
      if (this.resolvePromise)
        this.resolvePromise(false);
    }
  }
};
</script>
